import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { isCommentsModeActive } from 'js/file_viewer/core/atoms/comments';
import { activeEditorIdAtom, editorActivationParamsAtom } from 'js/file_viewer/core/atoms/editing';
import { activeTextHighlighterIdAtom, leftSidebarIdAtom, rightSidebarIdAtom, } from 'js/file_viewer/core/atoms/plugins';
import { sidebarWillAutoOpenAtom } from 'js/file_viewer/core/atoms/plugins';
import { audioVideoAreControlsHiddenAtom, audioVideoCurrentTimestampAtom, audioVideoIsDefaultAudioTrackSelectedAtom, audioVideoIsPlayingAtom, } from 'js/file_viewer/core/atoms/ui/audio_video';
import { docCurrentPageIndexAtom } from 'js/file_viewer/core/atoms/ui/doc';
import { areToolsHiddenAtom, isFullScreenAtom, isSlideshowAtom, isTitleBarRenameActiveAtom, } from 'js/file_viewer/core/atoms/ui/general';
import { areLineNumbersHiddenAtom } from 'js/file_viewer/core/atoms/ui/text';
import { zoomPercentAtom } from 'js/file_viewer/core/atoms/ui/zoom';
import { useFileViewerAtom, useFileViewerAtomValue, usePreviewKeyAtomCallback, usePreviewKeyAtomValue, } from 'js/file_viewer/core/atoms/use_atom';
import { useCurrentPreviewMetadata } from 'js/file_viewer/core/queries/preview_metadata';
import { usePageCount } from 'js/file_viewer/core/queries/riviera_preview_types/utils/ssr_doc';
import { useCurrentPreviewKey } from 'js/file_viewer/hooks/selectors';
/**
 * Returns a bundle of data for the root document preview. If the root preview is not a document, this will return
 * undefined. If viewing a nested file in an archive, this will return undefined since the root preview is the archive.
 * @returns {DocUiData | undefined}
 *   PreviewUiData for a document preview, if the root preview is a document.
 */
export const useRootDocUi = () => {
    const scaleFactor = useZoomScaleFactor();
    return { scaleFactor };
};
/**
 * Returns a bundle of UI data about the root image preview. If the root preview is not an image, this will return
 * undefined. If viewing a nested file in an archive, this will return undefined since the root preview is the archive.
 * @returns {ImageUiData | undefined}
 *  PreviewUiData for an image preview, if the root preview is an image.
 */
export const useRootImageUi = () => {
    const scaleFactor = useZoomScaleFactor();
    return { scaleFactor };
};
/**
 * @returns Whether the audio video controls are hidden. False if the current preview is not an audio or video file.
 */
export const useAreAudioVideoControlsHidden = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    return usePreviewKeyAtomValue(audioVideoAreControlsHiddenAtom, currentPreviewKey);
};
/**
 * @returns The active left sidebar that is open, undefined if the the left sidebar is not currently opened.
 */
export const useLeftSidebarId = () => {
    return useFileViewerAtomValue(leftSidebarIdAtom);
};
/**
 * @returns The active right sidebar that is open, undefined if the the right sidebar is not currently opened.
 */
export const useRightSidebarId = () => {
    return useFileViewerAtomValue(rightSidebarIdAtom);
};
/**
 * @returns The page number being previewed in the current document, or 0 if not a doc preview.
 * @see useGetCurrentDocPageCallback For a hook to retrieve the current page using a callback, that does not fire on
 * page change
 */
export const useCurrentDocPage = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    return usePreviewKeyAtomValue(docCurrentPageIndexAtom, currentPreviewKey);
};
/**
 * @returns A callback returning the page number being previewed in the current document, or a callback returning 0
 * if not a doc preview.
 * @see useCurrentDocPage For a hook that always fires on page change
 */
export const useGetCurrentDocPageCallback = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    return usePreviewKeyAtomCallback(docCurrentPageIndexAtom, currentPreviewKey);
};
/**
 * @returns
 *   If the current preview is a document, returns the total number of pages in the document. Undefined if the current
 *   preview is not a document. If viewing a nested file within an archive, this will return the total number of pages
 *   in the nested file.
 */
export const useCurrentPageCount = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    const currentPreviewMetadata = useCurrentPreviewMetadata();
    return usePageCount(currentPreviewKey, currentPreviewMetadata);
};
/**
 * @returns The id of the active editor. undefined if no editor is currently active.
 */
export const useActiveEditorId = () => {
    return useFileViewerAtomValue(activeEditorIdAtom);
};
/**
 * @returns Whether the current preview is in fullscreen mode.
 */
export const useIsFullscreen = () => {
    return useFileViewerAtomValue(isFullScreenAtom);
};
/**
 * @returns Whether the current preview is in slideshow mode.
 */
export const useIsSlideshow = () => {
    return useFileViewerAtomValue(isSlideshowAtom);
};
/**
 * @returns Whether the tools are hidden in the current preview.
 */
export const useAreToolsHidden = () => {
    return useFileViewerAtomValue(areToolsHiddenAtom);
};
/**
 * @returns Props that editors can use when they activate. For example, which action they should default to when
 *   they open.
 */
export const useEditorActivationParams = () => {
    return useFileViewerAtomValue(editorActivationParamsAtom);
};
/**
 * @returns The id of the active text highlighter. undefined if no text highlighter is currently active.
 */
export const useActiveTextHighlighterId = () => {
    return useFileViewerAtomValue(activeTextHighlighterIdAtom);
};
/**
 * @returns True if the rename input is currently active in the title bar.
 */
export const useIsRenameActive = () => {
    return useFileViewerAtomValue(isTitleBarRenameActiveAtom);
};
/**
 * @returns The current zoom scale factor. If the user has explicitly zoomed the preview, that zoom level is returned.
 *   Otherwise, the percent zoom required for the preview to fit the viewport is returned.
 */
export const useZoomScaleFactor = () => {
    return useFileViewerAtomValue(zoomPercentAtom);
};
/**
 * @returns true if the sidebar will auto-open aka should not be animated on open and false if will not auto-open
 */
export const useSidebarWillAutoOpen = () => {
    return useFileViewerAtomValue(sidebarWillAutoOpenAtom);
};
/*
 * @returns Whether comments mode is active in the current preview.
 */
export const useIsCommentsModeActive = () => {
    const commentsModeActive = useFileViewerAtomValue(isCommentsModeActive);
    const rightSidebar = useRightSidebarId();
    return rightSidebar === 'comments' || commentsModeActive;
};
export const useSetIsCommentsModeActiveCallback = () => {
    const [_, setIsCommentsModeActive] = useFileViewerAtom(isCommentsModeActive);
    return setIsCommentsModeActive;
};
/**
 * @returns Whether line numbers are hidden in text previews.
 */
export const useAreLineNumbersHidden = () => {
    return useFileViewerAtomValue(areLineNumbersHiddenAtom);
};
export const useAudioVideoCurrentTimestamp = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    return usePreviewKeyAtomValue(audioVideoCurrentTimestampAtom, currentPreviewKey);
};
export const useAudioVideoIsPlaying = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    return usePreviewKeyAtomValue(audioVideoIsPlayingAtom, currentPreviewKey);
};
export const useAudioVideoIsDefaultAudioTrackSelected = () => {
    const currentPreviewKey = useCurrentPreviewKey();
    return usePreviewKeyAtomValue(audioVideoIsDefaultAudioTrackSelectedAtom, currentPreviewKey);
};
export const useIsTranscriptEditingEnabled = () => {
    const { isLoggedIn } = useFileViewerContext();
    return isLoggedIn;
};
//# sourceMappingURL=ui.preenvesnextjs.map
